import { useState, useEffect, useMemo } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { CreditCard, Loader2 } from "lucide-react";

import { Card } from "@/components/ui/card";
import { authFetch } from "@/lib/authFetch";
import { cn } from "@/lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// FIN-3 — BarChart de gastos nos últimos 6 meses por cartão.
// Cliente faz fetch /accounts/{id}/spending-history?months=6 pra cada
// cartão e soma por mês. Performance OK pra ≤10 cartões; pra mais,
// pivot pra um endpoint agregado.

const fmtBRL = (v) => Number(v ?? 0).toLocaleString("pt-BR", {
  style: "currency", currency: "BRL", maximumFractionDigits: 0,
});

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-surface-overlay border border-subtle rounded-lg px-3 py-2 shadow-2xl">
      <p className="text-micro text-foreground-muted mb-1 capitalize">{label}</p>
      <p className="text-caption font-semibold text-status-error tabular-nums">
        {fmtBRL(payload[0].value)}
      </p>
    </div>
  );
}

export default function GastosCartoesBarChart({ creditCards }) {
  const [history, setHistory] = useState({}); // { account_id: months[] }
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!creditCards || creditCards.length === 0) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const out = {};
        await Promise.all(creditCards.map(async (c) => {
          try {
            const r = await authFetch(`${API_URL}/api/accounts/${c.id}/spending-history?months=6`);
            if (r.ok) {
              const data = await r.json();
              out[c.id] = data.months || [];
            }
          } catch (_) { /* skip */ }
        }));
        if (!cancelled) setHistory(out);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [creditCards]);

  // Soma por mês (todos cartões juntos por enquanto — multi-select fica como
  // refinamento futuro se houver volume).
  const data = useMemo(() => {
    const byMonth = new Map();
    Object.values(history).forEach(months => {
      months.forEach(m => {
        const cur = byMonth.get(m.month) || { month: m.month, label: m.label, total: 0 };
        cur.total += Number(m.total || 0);
        byMonth.set(m.month, cur);
      });
    });
    return Array.from(byMonth.values()).sort((a, b) => a.month.localeCompare(b.month));
  }, [history]);

  const totalAll = data.reduce((acc, m) => acc + (m.total || 0), 0);

  return (
    <Card variant="elevated" className="glass-card glow-hover p-5 md:p-6 min-h-[280px] flex flex-col" data-testid="contas-bar-chart">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-base md:text-lg font-bold text-foreground">
          Gastos nos últimos 6 meses
        </h3>
        <span className="text-micro text-foreground-muted uppercase" style={{ letterSpacing: "0.10em" }}>
          {creditCards.length} cartão{creditCards.length === 1 ? "" : "ões"}
        </span>
      </div>
      <div className="flex-1 min-h-[220px]">
        {loading && data.length === 0 ? (
          <div className="h-full flex items-center justify-center gap-2 text-foreground-muted text-caption">
            <Loader2 className="w-4 h-4 animate-spin" /> carregando histórico…
          </div>
        ) : totalAll === 0 ? (
          <div className="h-full flex flex-col items-center justify-center gap-2 text-foreground-muted">
            <CreditCard className="w-8 h-8 opacity-40" aria-hidden="true" />
            <p className="text-caption">Sem dados de gastos no período</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="label" stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => fmtBRL(v).replace("R$ ", "")} width={50} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(0,212,255,0.05)" }} />
              <Bar dataKey="total" fill="#FF4D6D" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </Card>
  );
}
